import React, { useEffect, useState } from 'react';
import { Database, Save, Eye, EyeOff, CheckCircle, AlertTriangle, Loader2, Plug, Lock, Unlock, ShieldAlert } from 'lucide-react';
import { useApi, useUserContext } from './DashboardLayout';
import BackupSection from './BackupPage';

export default function MariaDbPage() {
  const [config, setConfig] = useState({
    host: '', port: 3306, database: '', username: '', password: '',
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [confirmPw, setConfirmPw] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const { apiFetch } = useApi();
  const { userType } = useUserContext() || {};
  const isSuperAdmin = userType === 1;

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    const r = await apiFetch('/api/settings/mariadb');
    const d = await r?.json();
    if (d) {
      setConfig(prev => ({ ...prev, ...d, password: '' }));
      setPasswordSet(d.password_set || false);
    }
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 6000); };

  const handleUnlock = () => {
    setConfirmDialog(true);
    setConfirmPw('');
    setConfirmError('');
  };

  const handleConfirmUnlock = async () => {
    if (!confirmPw) { setConfirmError('Bitte Passwort eingeben'); return; }
    try {
      const r = await apiFetch('/api/auth/verify-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: confirmPw }),
      });
      const d = await r?.json();
      if (d?.verified) {
        setEditMode(true);
        setConfirmDialog(false);
        setConfirmPw('');
        showFb('success', 'Bearbeitung freigeschaltet. Bitte vorsichtig ändern!');
      } else {
        setConfirmError('Falsches Passwort');
      }
    } catch {
      setConfirmError('Fehler bei der Überprüfung');
    }
  };

  const handleLock = () => {
    setEditMode(false);
    loadConfig();
  };

  const handleSave = async () => {
    if (!config.host) { showFb('error', 'Host ist erforderlich'); return; }
    setSaving(true);
    try {
      const r = await apiFetch('/api/settings/mariadb', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const d = await r?.json();
      if (d?.status) {
        showFb('success', d.message);
        setPasswordSet(true);
        setEditMode(false);
      } else {
        showFb('error', d?.detail || 'Fehler beim Speichern');
      }
    } catch {
      showFb('error', 'Verbindungsfehler');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await apiFetch('/api/settings/mariadb/test', { method: 'POST' });
      const d = await r?.json();
      if (d?.status) {
        showFb('success', d.message);
      } else {
        showFb('error', d?.detail || 'Verbindungstest fehlgeschlagen');
      }
    } catch {
      showFb('error', 'Verbindungsfehler');
    }
    setTesting(false);
  };

  const readOnlyField = (label, value, isMasked = false) => (
    <div className="form-group">
      <label className="form-label" style={{ color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      <div style={{
        padding: '0.6rem 0.75rem', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 6,
        border: '1px solid var(--border-color, #e0e0e0)', fontSize: 14, color: 'var(--text-primary)',
        fontFamily: isMasked ? 'inherit' : 'monospace',
      }}>
        {isMasked ? '••••••••••••' : (value || '—')}
      </div>
    </div>
  );

  const editableField = (label, key, type = 'text', placeholder = '') => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {key === 'password' ? (
        <div style={{ position: 'relative' }}>
          <input
            className="form-input"
            type={showPw ? 'text' : 'password'}
            value={config.password}
            onChange={e => setConfig(prev => ({ ...prev, password: e.target.value }))}
            placeholder={passwordSet ? '(gespeichert — leer lassen zum Beibehalten)' : placeholder}
            data-testid={`mariadb-${key}`}
          />
          <button type="button" onClick={() => setShowPw(!showPw)}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      ) : (
        <input
          className="form-input"
          type={type}
          value={config[key]}
          onChange={e => setConfig(prev => ({ ...prev, [key]: type === 'number' ? parseInt(e.target.value) || 0 : e.target.value }))}
          placeholder={placeholder}
          data-testid={`mariadb-${key}`}
        />
      )}
    </div>
  );

  return (
    <div data-testid="mariadb-page">
      <div className="page-header">
        <h2>MariaDB-Verbindung</h2>
        <p>Verbindung zur produktiven MariaDB-Datenbank auf Hetzner. Diese Datenbank ist die Grundlage für die Herzgruppensuche.</p>
      </div>

      {feedback && (
        <div className={`alert ${feedback.type === 'success' ? 'alert-success' : 'alert-error'}`} data-testid="mariadb-feedback"
          style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8,
            background: feedback.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            color: feedback.type === 'success' ? 'var(--success)' : 'var(--error)',
            border: `1px solid ${feedback.type === 'success' ? 'var(--success)' : 'var(--error)'}` }}>
          {feedback.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          <span style={{ fontSize: 13 }}>{feedback.text}</span>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => setConfirmDialog(false)}>
          <div style={{
            background: 'var(--bg-primary, #fff)', borderRadius: 12, padding: '2rem', maxWidth: 420, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
              <ShieldAlert size={22} style={{ color: 'var(--warning, #f59e0b)' }} />
              <h3 style={{ margin: 0, fontSize: 16 }}>Sicherheitsabfrage</h3>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
              Die MariaDB-Einstellungen sind geschützt, da sie die produktive Datenbank betreffen.
              Bitte geben Sie Ihr Administrator-Passwort ein, um die Bearbeitung freizuschalten.
            </p>
            <input
              className="form-input"
              type="password"
              value={confirmPw}
              onChange={e => { setConfirmPw(e.target.value); setConfirmError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleConfirmUnlock()}
              placeholder="Administrator-Passwort"
              autoFocus
              data-testid="mariadb-confirm-password"
              style={{ marginBottom: '0.5rem' }}
            />
            {confirmError && <p style={{ color: 'var(--error)', fontSize: 13, margin: '0 0 0.75rem' }}>{confirmError}</p>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setConfirmDialog(false)} data-testid="mariadb-confirm-cancel">Abbrechen</button>
              <button className="btn btn-primary" onClick={handleConfirmUnlock} data-testid="mariadb-confirm-unlock">
                <Unlock size={14} /> Freischalten
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Database size={18} style={{ color: 'var(--primary)' }} />
            <span>Verbindungseinstellungen</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {editMode ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--warning, #f59e0b)', fontWeight: 600 }}>
                <Unlock size={14} /> Bearbeitung aktiv
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                <Lock size={14} /> Schreibgeschützt
              </span>
            )}
          </div>
        </div>
        <div className="card-body">
          {editMode ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  {editableField('Host', 'host', 'text', 'z.B. db.meinserver.de')}
                </div>
                {editableField('Port', 'port', 'number', '3306')}
                {editableField('Datenbankname', 'database', 'text', 'z.B. bgpr_live')}
                {editableField('Benutzername', 'username', 'text', 'z.B. bgpr_user')}
                {editableField('Passwort', 'password', 'password', 'Datenbankpasswort')}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="mariadb-save-btn">
                  {saving ? <><Loader2 size={16} className="spin" /> Speichern...</> : <><Save size={16} /> Speichern</>}
                </button>
                <button className="btn" onClick={handleTest} disabled={testing} data-testid="mariadb-test-btn"
                  style={{ background: 'var(--info)', color: '#fff', border: 'none' }}>
                  {testing ? <><Loader2 size={16} className="spin" /> Teste...</> : <><Plug size={16} /> Verbindung testen</>}
                </button>
                <button className="btn" onClick={handleLock} data-testid="mariadb-lock-btn"
                  style={{ marginLeft: 'auto' }}>
                  <Lock size={14} /> Sperren
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  {readOnlyField('Host', config.host)}
                </div>
                {readOnlyField('Port', config.port)}
                {readOnlyField('Datenbankname', config.database)}
                {readOnlyField('Benutzername', config.username)}
                {readOnlyField('Passwort', null, true)}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <button className="btn" onClick={handleUnlock} data-testid="mariadb-unlock-btn"
                  style={{ background: 'var(--warning, #f59e0b)', color: '#fff', border: 'none' }}>
                  <Unlock size={14} /> Bearbeitung freischalten
                </button>
                <button className="btn" onClick={handleTest} disabled={testing} data-testid="mariadb-test-btn"
                  style={{ background: 'var(--info)', color: '#fff', border: 'none' }}>
                  {testing ? <><Loader2 size={16} className="spin" /> Teste...</> : <><Plug size={16} /> Verbindung testen</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="card-body" style={{ padding: '0.75rem 1.25rem' }}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
            <strong>Hinweis:</strong> Diese Datenbank ist die produktive Grundlage des Portals und der Herzgruppensuche.
            Änderungen an der Verbindung sind nur mit Passwortbestätigung möglich, um versehentliche Änderungen zu vermeiden.
          </p>
        </div>
      </div>

      {isSuperAdmin && (
        <div style={{ marginTop: '2rem' }}>
          <BackupSection />
        </div>
      )}
    </div>
  );
}
